/**
 * Client Detail Hook — data for the client detail panel
 *
 * Fetches nested lists (contacts, active leases, active transient stays)
 * for a single client. Only fires when the panel is open (enabled: !!clientId).
 */

import { useQuery } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { supabase } from '@/lib/supabase'

// ============================================
// Types
// ============================================

export interface ClientDetailLease {
  id: string
  contract_number: string
  status: string
  monthly_rate: number | null
  start_date: string | null
  end_date: string | null
  unit: { unit_code: string } | null
  location: { name: string } | null
}

export interface ClientDetailTransient {
  id: string
  booking_number: string
  status: string
  check_in: string | null
  expected_check_out: string | null
  rate_amount: number | null
  location: { name: string } | null
}

export interface ClientDetailContact {
  id: string
  first_name: string
  last_name: string
  email_primary: string | null
  phone_mobile: string | null
  phone_office: string | null
  job_title: string | null
  is_primary_contact: boolean | null
  is_billing_contact: boolean | null
  is_operations_contact: boolean | null
}

export interface ClientDetailAircraft {
  id: string
  tail_number: string
  manufacturer: string | null
  model: string | null
  aircraft_type: { name: string; manufacturer: string | null; model: string | null } | null
}

export interface ClientDetailData {
  contacts: ClientDetailContact[]
  leases: ClientDetailLease[]
  transients: ClientDetailTransient[]
  aircraft: ClientDetailAircraft[]
}

// ============================================
// Query Keys
// ============================================

export const clientDetailKeys = {
  all: ['client-detail'] as const,
  leases: (clientId: string) => [...clientDetailKeys.all, 'leases', clientId] as const,
  transients: (clientId: string) => [...clientDetailKeys.all, 'transients', clientId] as const,
  contacts: (clientId: string) => [...clientDetailKeys.all, 'contacts', clientId] as const,
  aircraft: (clientId: string) => [...clientDetailKeys.all, 'aircraft', clientId] as const,
}

// ============================================
// Hook
// ============================================

export function useClientDetail(clientId: string | null | undefined) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id
  const enabled = !!clientId && !!organizationId

  const contactsQuery = useQuery({
    queryKey: clientDetailKeys.contacts(clientId || ''),
    queryFn: async (): Promise<ClientDetailContact[]> => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email_primary, phone_mobile, phone_office, job_title, is_primary_contact, is_billing_contact, is_operations_contact')
        .eq('client_id', clientId!)
        .is('deleted_at', null)
        .order('is_primary_contact', { ascending: false })
        .order('last_name', { ascending: true })

      if (error) throw error
      return (data ?? []) as ClientDetailContact[]
    },
    enabled,
  })

  const leasesQuery = useQuery({
    queryKey: clientDetailKeys.leases(clientId || ''),
    queryFn: async (): Promise<ClientDetailLease[]> => {
      const { data, error } = await supabase
        .from('lease_contracts')
        .select(`
          id,
          contract_number,
          status,
          monthly_rate,
          start_date,
          end_date,
          unit:unit_id (unit_code),
          location:location_id (name)
        `)
        .eq('client_id', clientId!)
        .eq('organization_id', organizationId!)
        .in('status', ['active', 'expiring'])
        .is('deleted_at', null)
        .order('end_date', { ascending: true })

      if (error) throw error
      return (data ?? []).map((row) => ({
        ...row,
        unit: row.unit as unknown as { unit_code: string } | null,
        location: row.location as unknown as { name: string } | null,
      }))
    },
    enabled,
  })

  const transientsQuery = useQuery({
    queryKey: clientDetailKeys.transients(clientId || ''),
    queryFn: async (): Promise<ClientDetailTransient[]> => {
      const { data, error } = await supabase
        .from('transient_stays')
        .select(`
          id,
          booking_number,
          status,
          check_in,
          expected_check_out,
          rate_amount,
          location:location_id (name)
        `)
        .eq('client_id', clientId!)
        .eq('organization_id', organizationId!)
        .in('status', ['reserved', 'confirmed', 'checked_in'])
        .is('deleted_at', null)
        .order('check_in', { ascending: true })

      if (error) throw error
      return (data ?? []).map((row) => ({
        ...row,
        location: row.location as unknown as { name: string } | null,
      }))
    },
    enabled,
  })

  const aircraftQuery = useQuery({
    queryKey: clientDetailKeys.aircraft(clientId || ''),
    queryFn: async (): Promise<ClientDetailAircraft[]> => {
      const { data, error } = await supabase
        .from('aircraft')
        .select(`
          id,
          tail_number,
          manufacturer,
          model,
          aircraft_type:aircraft_type_id (name, manufacturer, model)
        `)
        .or(`owner_client_id.eq.${clientId},operator_client_id.eq.${clientId}`)
        .eq('organization_id', organizationId!)
        .is('deleted_at', null)
        .order('tail_number', { ascending: true })

      if (error) throw error
      return (data ?? []).map((row) => ({
        ...row,
        aircraft_type: row.aircraft_type as unknown as { name: string; manufacturer: string | null; model: string | null } | null,
      }))
    },
    enabled,
  })

  const isLoading = contactsQuery.isLoading || leasesQuery.isLoading || transientsQuery.isLoading || aircraftQuery.isLoading

  return {
    contacts: contactsQuery.data ?? [],
    leases: leasesQuery.data ?? [],
    transients: transientsQuery.data ?? [],
    aircraft: aircraftQuery.data ?? [],
    isLoading,
    isError: contactsQuery.isError || leasesQuery.isError || transientsQuery.isError || aircraftQuery.isError,
  }
}
